import React, { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import MarketHeader from "@/components/MarketHeader";
import { useMarket } from "@/lib/MarketContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { LogIn, Trash2, Check, Radio, RefreshCw, Wifi, Copy, ExternalLink } from "lucide-react";

const BROKERS = [
  {
    key: "fyers",
    label: "Fyers (API v3)",
    accent: "text-[#f0b90b]",
    loginLabel: "Login with Fyers",
    devConsole: "https://myapi.fyers.in/dashboard",
    help: "Create an app at myapi.fyers.in. Set the redirect URL below as your app's Redirect URI, then click Login.",
  },
  {
    key: "zerodha",
    label: "Zerodha (Kite Connect)",
    accent: "text-[#387ed1]",
    loginLabel: "Login with Zerodha",
    devConsole: "https://developers.kite.trade/apps",
    help: "Create an app at developers.kite.trade. Set the redirect URL below as your app's Redirect URL, then click Login.",
  },
  {
    key: "upstox",
    label: "Upstox (Uplink v2)",
    accent: "text-[#732BE0]",
    loginLabel: "Login with Upstox",
    devConsole: "https://account.upstox.com/developer/apps",
    help: "Create an app at account.upstox.com/developer/apps. Set the redirect URL below as your Redirect URI, then click Login.",
  },
];

const emptyForms = () =>
  Object.fromEntries(BROKERS.map((b) => [b.key, { api_key: "", api_secret: "" }]));

const POPUP_W = 520;
const POPUP_H = 680;

// The redirect URI that must be registered in each broker's developer console
const REDIRECT_BASE = window.location.origin;
const redirectUri = (broker) => `${REDIRECT_BASE}/broker-callback/${broker}`;

// Small reusable component that shows the redirect URL the user must paste
// into their broker developer console
function RedirectUrlBox({ broker, devConsole }) {
  const url = redirectUri(broker);
  const [copied, setCopied] = useState(false);

  const copy = () => {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="rounded-sm border border-amber-500/20 bg-amber-500/5 p-2.5 space-y-1.5">
      <div className="flex items-center justify-between gap-1">
        <span className="text-[10px] uppercase tracking-widest text-amber-400/80 font-semibold">
          Required: set this as Redirect URL in your broker app
        </span>
        <a
          href={devConsole}
          target="_blank"
          rel="noreferrer"
          className="text-[10px] text-slate-500 hover:text-slate-300 flex items-center gap-0.5 shrink-0"
        >
          Open console <ExternalLink className="w-2.5 h-2.5" />
        </a>
      </div>
      <div className="flex items-center gap-1.5">
        <span className="font-mono text-[10px] text-slate-300 flex-1 break-all leading-relaxed">{url}</span>
        <button
          onClick={copy}
          className="shrink-0 p-1 rounded hover:bg-white/10 text-slate-400 hover:text-slate-200 transition-colors"
          title="Copy redirect URL"
        >
          {copied
            ? <Check className="w-3 h-3 text-emerald-400" />
            : <Copy className="w-3 h-3" />}
        </button>
      </div>
    </div>
  );
}

export default function BrokerSettings() {
  const { refreshBrokers } = useMarket();
  const [configs, setConfigs] = useState([]);
  const [forms, setForms] = useState(emptyForms);
  const [saving, setSaving] = useState({});
  const [logging, setLogging] = useState({});
  const popupRefs = useRef({});
  const pollRefs = useRef({});

  const load = useCallback(() =>
    api.get("/broker/configs").then((r) => setConfigs(r.data)).catch(() => {}),
  []);

  useEffect(() => { load(); }, [load]);

  // postMessage from OAuthCallback popup → success
  useEffect(() => {
    const handler = (e) => {
      if (e.data?.type !== "BROKER_AUTH_SUCCESS") return;
      const broker = e.data.broker;
      if (pollRefs.current[broker]) {
        clearInterval(pollRefs.current[broker]);
        delete pollRefs.current[broker];
      }
      setLogging((prev) => ({ ...prev, [broker]: false }));
      toast.success(`${broker} connected — access token saved!`, { duration: 4000 });
      load();
      refreshBrokers(); // update the header immediately
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [load, refreshBrokers]);

  const setField = (broker, field, val) =>
    setForms((prev) => ({ ...prev, [broker]: { ...prev[broker], [field]: val } }));

  // Step 1 — save API key + secret
  const saveCredentials = async (broker) => {
    const f = forms[broker];
    if (!f.api_key || !f.api_secret) {
      toast.error("API key and secret are required");
      return false;
    }
    setSaving((prev) => ({ ...prev, [broker]: true }));
    try {
      await api.post("/broker/configs", {
        broker,
        api_key: f.api_key,
        api_secret: f.api_secret,
        use_for_market: false,
      });
      setForms((prev) => ({ ...prev, [broker]: { api_key: "", api_secret: "" } }));
      await load();
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save credentials");
      return false;
    } finally {
      setSaving((prev) => ({ ...prev, [broker]: false }));
    }
  };

  // Step 2 — open OAuth popup
  const startOAuth = async (broker) => {
    const existing = configs.find((c) => c.broker === broker);
    if (!existing) {
      const ok = await saveCredentials(broker);
      if (!ok) return;
    }

    setLogging((prev) => ({ ...prev, [broker]: true }));
    try {
      const { data } = await api.get("/broker/oauth/login-url", { params: { broker } });

      const left = Math.round(window.screenX + (window.outerWidth - POPUP_W) / 2);
      const top = Math.round(window.screenY + (window.outerHeight - POPUP_H) / 2);
      const popup = window.open(
        data.url,
        `broker_oauth_${broker}`,
        `width=${POPUP_W},height=${POPUP_H},left=${left},top=${top},` +
          `toolbar=no,menubar=no,scrollbars=yes,resizable=yes`
      );

      if (!popup || popup.closed) {
        toast.error("Popup blocked — please allow popups for this site and try again.");
        setLogging((prev) => ({ ...prev, [broker]: false }));
        return;
      }

      popupRefs.current[broker] = popup;

      // Poll for popup close — fallback when postMessage isn't fired
      pollRefs.current[broker] = setInterval(async () => {
        let closed = false;
        let landedOnOurApp = false;

        try {
          if (popup.closed) {
            closed = true;
          } else {
            // Same-origin check: if the popup navigated back to our app
            // it means the broker's redirect URL isn't configured correctly
            try {
              const popupPath = popup.location.pathname;
              if (popupPath && !popupPath.startsWith("/broker-callback")) {
                landedOnOurApp = true;
              }
            } catch (_) {
              // cross-origin (broker page) — that's fine, still loading
            }
          }
        } catch (_) {
          closed = true;
        }

        if (landedOnOurApp) {
          clearInterval(pollRefs.current[broker]);
          delete pollRefs.current[broker];
          setLogging((prev) => ({ ...prev, [broker]: false }));
          toast.error(
            `${broker} login redirected to the wrong page. ` +
            `In your ${broker} developer console, set the Redirect URL to: ${redirectUri(broker)}`,
            { duration: 10000 }
          );
          return;
        }

        if (!closed) return;

        clearInterval(pollRefs.current[broker]);
        delete pollRefs.current[broker];
        setLogging((prev) => ({ ...prev, [broker]: false }));

        // Check if the broker is now connected
        try {
          const r = await api.get("/broker/configs");
          setConfigs(r.data);
          const updated = r.data.find((c) => c.broker === broker);
          if (updated?.is_connected) {
            toast.success(`${broker} connected — access token saved!`, { duration: 4000 });
            refreshBrokers();
          } else {
            toast.warning(`${broker} login window closed without completing. Try again.`);
          }
        } catch (_) {}
      }, 500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start OAuth login");
      setLogging((prev) => ({ ...prev, [broker]: false }));
    }
  };

  const useForMarket = async (broker) => {
    try {
      await api.post(`/broker/configs/${broker}/use-for-market`);
      toast.success(`${broker} set as live market data source`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const remove = async (broker) => {
    if (!window.confirm(`Remove ${broker} credentials?`)) return;
    await api.delete(`/broker/configs/${broker}`);
    load();
    refreshBrokers();
    toast.success("Removed");
  };

  // Cleanup polls on unmount
  useEffect(() => {
    return () => {
      Object.values(pollRefs.current).forEach(clearInterval);
    };
  }, []);

  return (
    <div className="flex flex-col min-h-screen" data-testid="broker-settings-page">
      <MarketHeader />
      <div className="p-3 sm:p-6 space-y-4 sm:space-y-6 max-w-5xl">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">Broker Settings</h1>
          <p className="text-sm text-slate-400 mt-0.5">
            Connect Fyers, Zerodha, or Upstox. Enter your API key &amp; secret, then click{" "}
            <span className="text-blue-300">Login with Broker</span> — your access token is captured automatically each day.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {BROKERS.map((b) => {
            const existing = configs.find((c) => c.broker === b.key);
            const isLogging = !!logging[b.key];
            const isSaving = !!saving[b.key];

            return (
              <Card
                key={b.key}
                className={`border rounded-sm p-5 space-y-4 transition-colors ${
                  existing?.is_connected
                    ? "border-emerald-500/30 bg-[#0f1a12]"
                    : "border-white/10 bg-[#141414]"
                }`}
                data-testid={`broker-card-${b.key}`}
              >
                {/* Header */}
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h3 className={`font-heading font-semibold text-base ${b.accent}`}>{b.label}</h3>
                    <p className="text-xs text-slate-500 mt-1 leading-relaxed">{b.help}</p>
                  </div>

                  {/* Status badges */}
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    {existing ? (
                      <>
                        <span
                          className={`px-1.5 py-0.5 rounded-sm text-[10px] uppercase tracking-widest font-semibold ${
                            existing.is_connected
                              ? "bg-emerald-500/20 text-emerald-400"
                              : "bg-amber-500/15 text-amber-400"
                          }`}
                        >
                          {existing.is_connected ? "✓ Connected" : "Saved"}
                        </span>
                        {existing.use_for_market && (
                          <span className="px-1.5 py-0.5 rounded-sm text-[10px] uppercase tracking-widest bg-blue-500/15 text-blue-300 flex items-center gap-1">
                            <Wifi className="w-3 h-3" /> Live feed
                          </span>
                        )}
                      </>
                    ) : (
                      <span className="px-1.5 py-0.5 rounded-sm text-[10px] uppercase tracking-widest text-slate-600">
                        Not set up
                      </span>
                    )}
                  </div>
                </div>

                {/* Body */}
                {existing ? (
                  <div className="space-y-2 border-t border-white/5 pt-3">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-slate-500 uppercase tracking-widest">API Key</span>
                      <span className="font-mono text-slate-300">{existing.api_key_masked}</span>
                    </div>

                    {/* Redirect URL reminder */}
                    <RedirectUrlBox broker={b.key} devConsole={b.devConsole} />

                    {/* Login / refresh token */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => startOAuth(b.key)}
                      disabled={isLogging}
                      data-testid={`refresh-token-${b.key}`}
                      className={`w-full ${
                        existing.is_connected
                          ? "border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10"
                          : "border-blue-500/40 text-blue-300 hover:bg-blue-500/10"
                      }`}
                    >
                      {isLogging ? (
                        <><RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Waiting for login…</>
                      ) : existing.is_connected ? (
                        <><RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh today's token</>
                      ) : (
                        <><LogIn className="w-3.5 h-3.5 mr-1.5" /> {b.loginLabel}</>
                      )}
                    </Button>

                    {/* Set as live feed — only show if connected but not yet the active feed */}
                    {existing.is_connected && !existing.use_for_market && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => useForMarket(b.key)}
                        data-testid={`use-market-${b.key}`}
                        className="border-blue-500/40 text-blue-300 hover:bg-blue-500/10 w-full"
                      >
                        <Radio className="w-3.5 h-3.5 mr-1.5" /> Use for live market data
                      </Button>
                    )}

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => remove(b.key)}
                      data-testid={`remove-broker-${b.key}`}
                      className="border-rose-500/40 text-rose-400 hover:bg-rose-500/10 w-full"
                    >
                      <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Remove &amp; re-enter
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {/* Redirect URL — must be configured in broker's dev console first */}
                    <RedirectUrlBox broker={b.key} devConsole={b.devConsole} />

                    <div className="space-y-1.5">
                      <Label className="text-xs uppercase tracking-widest text-slate-400">
                        {b.key === "fyers" ? "Client ID / App ID" : "API Key"}
                      </Label>
                      <Input
                        value={forms[b.key].api_key}
                        onChange={(e) => setField(b.key, "api_key", e.target.value)}
                        data-testid={`${b.key}-api-key-input`}
                        className="bg-[#0d0d0d] border-white/10 h-9 font-mono"
                        placeholder={b.key === "fyers" ? "XXXXXX-100" : "xxxxxx"}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs uppercase tracking-widest text-slate-400">
                        {b.key === "fyers" ? "Secret Key" : "API Secret"}
                      </Label>
                      <Input
                        type="password"
                        value={forms[b.key].api_secret}
                        onChange={(e) => setField(b.key, "api_secret", e.target.value)}
                        data-testid={`${b.key}-api-secret-input`}
                        className="bg-[#0d0d0d] border-white/10 h-9 font-mono"
                        placeholder="••••••"
                      />
                    </div>

                    <Button
                      onClick={() => startOAuth(b.key)}
                      disabled={isLogging || isSaving}
                      data-testid={`login-broker-${b.key}`}
                      className="bg-blue-500 hover:bg-blue-400 text-black font-semibold w-full"
                    >
                      {isLogging ? (
                        <><RefreshCw className="w-4 h-4 mr-1.5 animate-spin" /> Waiting for login…</>
                      ) : isSaving ? (
                        <><RefreshCw className="w-4 h-4 mr-1.5 animate-spin" /> Saving…</>
                      ) : (
                        <><LogIn className="w-4 h-4 mr-1.5" /> {b.loginLabel}</>
                      )}
                    </Button>
                  </div>
                )}
              </Card>
            );
          })}
        </div>

        {/* How it works */}
        <Card className="border-white/10 bg-[#141414] rounded-sm p-4">
          <h3 className="font-heading font-semibold text-sm mb-2 flex items-center gap-2">
            <Check className="w-4 h-4 text-emerald-400" /> How live data &amp; trading works
          </h3>
          <ul className="text-xs text-slate-400 space-y-1.5 list-disc list-inside">
            <li>Enter your API key &amp; secret once. Every day, click <span className="text-blue-300">Login with Broker</span> — a popup opens, you log in, and your access token is saved automatically.</li>
            <li>The first broker you connect is automatically set as the live market data source.</li>
            <li>Ticker, option chain, and mark-to-market P&amp;L update from the broker marked <span className="text-blue-300">Live feed</span> only.</li>
            <li>Without a connected access token the app shows no prices.</li>
            <li>Set each strategy's broker + <span className="text-slate-200 font-mono">live</span> / <span className="text-amber-400 font-mono">paper</span> mode on the Strategies page.</li>
            <li>Access tokens expire daily — click the login button each morning before trading.</li>
          </ul>
        </Card>
      </div>
    </div>
  );
}
